import React, { forwardRef } from 'react';
import styles from './FormInput.module.css';

/**
 * PhoneNumberInput Component
 * 
 * Automatically formats phone numbers to (XXX) XXX-XXXX format.
 * Accepts any input format and cleans it up on blur.
 * 
 * Features:
 * - Real-time formatting as user types
 * - Auto-correction on blur (tab out)
 * - Accepts any phone number format (dashes, parentheses, spaces, etc.)
 * - Validates minimum 10 digits
 * - Returns formatted value: (XXX) XXX-XXXX
 */
const PhoneNumberInput = forwardRef(({
    label,
    name,
    value = '',
    onChange,
    onBlur,
    error,
    required = false,
    placeholder = '(555) 123-4567',
    disabled = false,
    className = '',
    style = {},
    icon,
    helpText,
    ...props
}, ref) => {
    const inputId = `input-${name}`;

    /**
     * Extract only digits from a string
     */
    const extractDigits = (str) => {
        return str.replace(/\D/g, '');
    };

    /**
     * Format digits into (XXX) XXX-XXXX format
     * Handles partial numbers gracefully
     */
    const formatPhoneNumber = (digits) => {
        if (!digits) return '';

        // Remove all non-digits
        const cleaned = extractDigits(digits);

        // Limit to first 10 digits
        const first10 = cleaned.slice(0, 10);

        // Format based on how many digits we have
        if (first10.length === 0) return '';
        if (first10.length <= 3) {
            return `(${first10}`;
        }
        if (first10.length <= 6) {
            return `(${first10.slice(0, 3)}) ${first10.slice(3)}`;
        }
        return `(${first10.slice(0, 3)}) ${first10.slice(3, 6)}-${first10.slice(6)}`;
    };

    /**
     * Handle input change - format as user types
     */
    const handleChange = (e) => {
        const rawValue = e.target.value;
        const formatted = formatPhoneNumber(rawValue);

        // Create synthetic event with formatted value
        const syntheticEvent = {
            target: {
                ...e.target,
                value: formatted,
                name,
            },
        };

        onChange(syntheticEvent);
    };

    /**
     * Handle blur - ensure valid format, clean up if incomplete
     */
    const handleBlur = (e) => {
        const rawValue = e.target.value;
        const digits = extractDigits(rawValue);

        let finalValue = '';

        // Only keep if we have at least 10 digits
        if (digits.length >= 10) {
            finalValue = formatPhoneNumber(digits);
        } else if (digits.length > 0 && digits.length < 10) {
            // Partial entry - leave as is but still formatted
            finalValue = formatPhoneNumber(digits);
        }

        // If value changed during blur, update parent
        if (finalValue !== value) {
            const syntheticEvent = {
                target: {
                    value: finalValue,
                    name,
                },
            };
            onChange(syntheticEvent);
        }

        // Call original onBlur if provided
        if (onBlur) {
            onBlur(e);
        }
    };

    return (
        <div className={`${styles.container} ${className}`} style={style}>
            {label && (
                <label htmlFor={inputId} className={styles.label}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}

            <div className={styles.inputWrapper}>
                <input
                    ref={ref}
                    id={inputId}
                    type="tel"
                    name={name}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    className={`${styles.input} ${error ? styles.hasError : ''}`}
                    autoComplete="tel"
                    {...props}
                />
                {icon && <span className={styles.icon}>{icon}</span>}
            </div>

            {helpText && !error && (
                <div className={styles.helpText}>
                    {helpText}
                </div>
            )}

            {error && (
                <div className={styles.error} role="alert">
                    {error}
                </div>
            )}
        </div>
    );
});

PhoneNumberInput.displayName = 'PhoneNumberInput';

export { PhoneNumberInput };
export default PhoneNumberInput;
